import { jsx as _jsx } from "react/jsx-runtime";
// IIFE entry: mounts a shadow root on document.body and renders the
// popup tree against server-arbitrated config + data-lk-* overrides.
import { createRoot } from 'react-dom/client';
import { PortalContainerContext } from '@repo/bytes-react';
import { fetchServerConfig, resolveConfig, resolveTheme, watchSystemTheme, } from '@/components/embed-popup/embed-config';
import { parseScriptDataset } from '@/lib/embed/parse-script-dataset';
import { AgentClient } from './agent-client';
import embedPopupCss from './embed-popup.css';
function bundleOrigin(script) {
    if (!script.src)
        return null;
    try {
        return new URL(script.src).origin;
    }
    catch {
        return null;
    }
}
async function bootstrap(themeOuter, themeInner, apiBase, parsed) {
    const outcome = await fetchServerConfig(apiBase, parsed.agent);
    if (outcome.status !== 'ok') {
        if (outcome.status === 'denied') {
            // eslint-disable-next-line no-console
            console.error('embed-popup: LiveKit embed is disabled or not allowed from this origin.');
        }
        else {
            // eslint-disable-next-line no-console
            console.error('embed-popup: failed to load config:', outcome.error);
        }
        return;
    }
    const resolved = resolveConfig(outcome.config, parsed);
    applyTheme(themeOuter, themeInner, resolveTheme(resolved.theme));
    watchSystemTheme(resolved.theme, (next) => applyTheme(themeOuter, themeInner, next));
    renderTree(themeInner, apiBase, parsed.agent, resolved);
}
function applyTheme(themeOuter, themeInner, theme) {
    themeInner.setAttribute('data-lk-theme', theme);
    // embed-popup's dark variant is `.dark .embed-popup` (descendant).
    themeOuter.classList.toggle('dark', theme === 'dark');
}
function renderTree(themeWrapper, apiBase, agentId, resolved) {
    // Redirect Radix portals (Select/Popover/DropdownMenu/Tooltip/Dialog)
    // to the shadow-root theme wrapper so the popper renders inside the
    // shadow root — it picks up the bundled Tailwind utilities and stacks
    // inside the widget's own context instead of the host page's.
    createRoot(themeWrapper).render(_jsx(PortalContainerContext.Provider, { value: themeWrapper, children: _jsx(AgentClient, { apiBase: apiBase, agentId: agentId, config: resolved }) }));
}
function main() {
    const script = document.currentScript;
    if (!(script instanceof HTMLScriptElement)) {
        // eslint-disable-next-line no-console
        console.error('embed-popup: unable to find loading <script> element. The bundle must be loaded as a classic script (not a module or via XHR/fetch+eval).');
        return;
    }
    if (window.__LK_EMBED_MOUNTED__) {
        // eslint-disable-next-line no-console
        console.warn('embed-popup: bundle loaded more than once on this page; ignoring duplicate.');
        return;
    }
    window.__LK_EMBED_MOUNTED__ = true;
    let parsed;
    try {
        parsed = parseScriptDataset(script.dataset);
    }
    catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        // eslint-disable-next-line no-console
        console.error('embed-popup:', message);
        return;
    }
    const apiBase = bundleOrigin(script);
    if (apiBase === null) {
        // eslint-disable-next-line no-console
        console.error('embed-popup: unable to derive API base URL from the loading <script src=...>.');
        return;
    }
    const wrapper = document.createElement('div');
    wrapper.id = 'lk-embed-wrapper';
    document.body.appendChild(wrapper);
    const shadowRoot = wrapper.attachShadow({ mode: 'open' });
    const styleTag = document.createElement('style');
    styleTag.textContent = embedPopupCss;
    shadowRoot.appendChild(styleTag);
    // Two wrappers: the outer toggles `.dark` (embed-popup dark variant
    // matches `.dark .embed-popup` as a descendant), the inner carries
    // `.embed-popup` + `data-lk-theme` so bytes-core and shadcn tokens
    // both resolve. Default to light to avoid a wrong-palette flash
    // before the config fetch lands.
    const themeOuter = document.createElement('div');
    shadowRoot.appendChild(themeOuter);
    const themeInner = document.createElement('div');
    themeInner.classList.add('embed-popup');
    themeInner.setAttribute('data-lk-theme', 'light');
    themeOuter.appendChild(themeInner);
    void bootstrap(themeOuter, themeInner, apiBase, parsed);
}
main();
